/**
 * BriefingCanvas — the persistent additive canvas for all 4 Briefing acts.
 *
 * The canvas never resets between acts. Act 1's fingerprint chips persist
 * into Act 2 as the constellation's frame. Act 2's constellation persists
 * into Act 3 with new colors + a time axis. Act 3's burst schedule extends
 * downward into the predicted curve. Act 4 ignites and hands off to the
 * Live Wave Dashboard. This is THE architectural rule that makes this a
 * cinematic moment rather than a 4-step wizard.
 *
 * Layers (z-stack, top → bottom of viewport):
 *   1. Header strip — "Wave Briefing · Helix Motors · {wave_name}"
 *   2. ACT 1 zone — fingerprint chips (sticky-row at top of canvas)
 *   3. ACT 2 zone — constellation + integrity checks (center of canvas)
 *   4. ACT 3 zone — time axis + predicted curve (lower-middle)
 *   5. ACT 4 zone — Pulse meter materialization (top-right of canvas)
 *   6. Footer — narrator line ("Pulled 40 creators from your DNA…")
 *
 * Acts 2, 3, 4 currently render as `null` (placeholder). The shell ships
 * with the slots laid out so each act drops in without rearchitecting.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { ChevronLeft, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Act1Dna from './Act1Dna';
import RadialNetwork from './RadialNetwork';
import BriefingLoadingState from './BriefingLoadingState';
import IgnitionFireButton from './IgnitionFireButton';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BriefingCanvas({
  briefing,
  brandName,
  waveName,
  onIgnite, // eslint-disable-line no-unused-vars
}) {
  const navigate = useNavigate();
  const [act, setAct] = useState(1);
  const [narratorLine, setNarratorLine] = useState('Matching creators to your brand fingerprint…');

  // Ring-tick state — which integrity ring in the hero is currently
  // lit up. Drives the live verdict ticking effect in CMD INTEGRITY
  // tile during Act 2.
  const [tickedRingKeys, setTickedRingKeys] = useState(new Set());

  // ─── Act 4 state ─────────────────────────────────────────────
  // RadialNetwork settles → Fire button appears → click triggers
  // countdown (3 → 2 → 1) → ignition (PZ bloom + edges → pink +
  // first curve segment → solid pink + PulseMeter materializes) →
  // navigate to /waves/{id}/control. The radial network never
  // unmounts — the visual state is additive.
  const [act3Settled, setAct3Settled] = useState(false);
  const [act4Phase, setAct4Phase] = useState('idle'); // 'idle' | 'countdown' | 'igniting' | 'handoff'
  const [countdownN, setCountdownN] = useState(null);
  // Integrity warning gate — when the cohort has at least one failing
  // structural check, the Fire button opens a soft confirmation step
  // ("Your cohort has a weak structural check. Fire anyway?") instead
  // of going straight to countdown. Brand stays in control, sees the
  // risk. Open product question Q1 from the iter-88 handoff.
  const [integrityWarnOpen, setIntegrityWarnOpen] = useState(false);
  // Live pulse pre-polled during the ~600ms ignition→handoff window so
  // the Control Room's Pulse meter has a non-zero value on arrival.
  // Open product question Q3 from the iter-88 handoff.
  const [livePulse, setLivePulse] = useState(null);
  const handoffTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const pulsePollTimerRef = useRef(null);

  const [skipAllowed, setSkipAllowed] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setSkipAllowed(true), 2000);
    return () => clearTimeout(t);
  }, []);

  // Clean up any in-flight timers on unmount
  useEffect(() => () => {
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    if (handoffTimerRef.current) clearTimeout(handoffTimerRef.current);
    if (pulsePollTimerRef.current) clearInterval(pulsePollTimerRef.current);
  }, []);

  // Defensive route guard — open product question Q2 from the iter-88
  // handoff. If the brand somehow lands on the briefing route for a
  // wave that's already live, completed, or sent, redirect them straight
  // to the Control Room so we never re-ignite. The cinematic only plays
  // for waves still in 'draft' or 'scheduled' status.
  useEffect(() => {
    if (!briefing?.wave_id) return undefined;
    const status = (briefing?.wave_status || '').toLowerCase();
    const lockedStatuses = ['live', 'sent', 'active', 'completed', 'paused'];
    if (lockedStatuses.includes(status)) {
      navigate(`/waves/${briefing.wave_id}/control`, { replace: true });
    }
    return undefined;
  }, [briefing?.wave_id, briefing?.wave_status, navigate]);

  const handleSkip = useCallback(() => {
    localStorage.setItem('briefing_seen_once', '1');
    setAct(4);
    setAct3Settled(true);
    setNarratorLine('Briefing ready for review. Fire when ready.');
  }, []);

  // Act 1 → Radial Network handoff. Narrator update is intentionally a
  // teaser — the network animation will rewrite it as each stage fires.
  const handleAct1Complete = useCallback(() => {
    if (!briefing) return;
    const a1 = briefing.act1;
    setNarratorLine(
      `${a1.candidates.length} micro-creators matched your fingerprint. ` +
      `Now testing structural integrity…`,
    );
    // Auto-advance to Act 2 (RadialNetwork) after a brief beat — the
    // brand reads the gallery for a moment before the shields lift
    // off and reorganize into the network.
    const t = setTimeout(() => setAct(2), 900);
    return () => clearTimeout(t);
  }, [briefing]);

  // RadialNetwork — each integrity check tick lights up the matching
  // ring in the CMD INTEGRITY hero tile.
  const handleRadialCheckTick = useCallback(({ key }) => {
    setTickedRingKeys(prev => new Set(prev).add(key));
  }, []);

  // Act 2 portion — composes the ACTIONABLE narrator from the failing
  // check's diagnosis + suggested action. Auto-advances to Act 3.
  const handleAct2Complete = useCallback((failingCheck) => {
    if (!briefing) return;
    const a1 = briefing.act1;
    const integrity = briefing.act2?.integrity || { passed: 3, total: 3 };
    if (!failingCheck) {
      setNarratorLine(
        `${a1.candidates.length} micro-creators · ` +
        `Cohort passed all 3 structural checks. Ready to fire.`,
      );
    } else {
      const action = failingCheck.suggested_action || '';
      const actionSentence = action
        ? action.charAt(0).toUpperCase() + action.slice(1) + '.'
        : '';
      setNarratorLine(
        `Cohort passed ${integrity.passed} of ${integrity.total} structural checks — ` +
        `${failingCheck.diagnosis}. ${actionSentence}`,
      );
    }
    // Act 3 begins inside the same RadialNetwork — no act state change
    // needed; the unified component continues its timeline.
  }, [briefing]);

  // Act 3 stages — each stage rewrites the narrator with what's
  // happening live. Same standard as Act 2: every state names the
  // action, not just the outcome.
  const handleRadialStageChange = useCallback((stage) => {
    if (!briefing) return;
    const a3 = briefing.act3 || {};
    const pz = a3.patient_zero;
    switch (stage) {
      case 'centrality_scan':
        setNarratorLine('Computing influence centrality across the cohort…');
        break;
      case 'pz_reveal':
        setNarratorLine(
          pz
            ? `Patient Zero identified — ${pz.name}. ${pz.match}% brand match, centrality ${pz.centrality_score}.`
            : 'Patient Zero identified.'
        );
        break;
      case 'roles':
        setNarratorLine('Assigning roles — bridges propagate, amplifiers saturate, long-tail extends.');
        break;
      case 'axis':
        setNarratorLine('Scheduling 4 bursts across 72 hours.');
        break;
      case 'curve':
        setNarratorLine('Plotting the predicted compounding curve.');
        break;
      case 'settled':
        setNarratorLine(
          a3.compounding_label
            ? `${a3.compounding_label}. Fire the wave to ignite Patient Zero.`
            : 'Burst schedule locked. Fire the wave to ignite Patient Zero.'
        );
        setAct3Settled(true);
        break;
      default:
        break;
    }
  }, [briefing]);

  const handleRadialComplete = useCallback(() => {
    setAct3Settled(true);
  }, []);

  // ─── Act 4 handlers — countdown + ignition + handoff ─────────
  //
  // Two click paths from the Fire button:
  //   • cohort integrity all-green  → goes straight into the countdown
  //   • any failing check           → opens a soft warning ("Your cohort
  //     has a weak structural check. Fire anyway?"). Brand confirms or
  //     cancels. No hard block — they stay in control.
  const hasFailingCheck = useCallback(() => {
    const checks = briefing?.act2?.integrity?.checks || [];
    return checks.some(c => c.passed === false);
  }, [briefing]);

  const startCountdown = useCallback(() => {
    if (!briefing) return;
    setIntegrityWarnOpen(false);
    setAct4Phase('countdown');
    setCountdownN(3);
    setNarratorLine('Ignition sequence engaged. 3…');
    let n = 3;
    countdownTimerRef.current = setInterval(() => {
      n -= 1;
      if (n > 0) {
        setCountdownN(n);
        setNarratorLine(`Ignition sequence engaged. ${n}…`);
      } else {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
        setCountdownN(null);
        setAct4Phase('igniting');
        const pz = briefing.act3?.patient_zero;
        setNarratorLine(
          pz
            ? `Wave igniting — ${pz.name} fires. The cascade is live.`
            : 'Wave igniting — Patient Zero fires. The cascade is live.'
        );
        // Pre-poll pulse during the 2.6s ignition window so the Control
        // Room's Pulse meter has a non-zero value on arrival rather
        // than freezing at 0 waiting for its own /pulse fetch. We poll
        // every 250ms — the first hit usually returns within 200ms
        // because the wave engine publishes pulse on a 30s tick, so
        // the latest cached value is read out fast.
        let attempts = 0;
        pulsePollTimerRef.current = setInterval(async () => {
          attempts += 1;
          try {
            const r = await axios.get(
              `${API}/api/brand/waves/${briefing.wave_id}/pulse`,
              { withCredentials: true },
            );
            const score = r?.data?.pulse_score;
            if (Number.isFinite(score) && score > 0) {
              setLivePulse(score);
              clearInterval(pulsePollTimerRef.current);
              pulsePollTimerRef.current = null;
            }
          } catch (_e) { /* swallow — pulse may not be ready yet */ }
          // Stop polling after 8 attempts (~2s) to avoid leaking timers.
          if (attempts >= 8) {
            clearInterval(pulsePollTimerRef.current);
            pulsePollTimerRef.current = null;
          }
        }, 250);

        // Settle for 2.6s of ignition cinematics, then hand off to the
        // Wave Control Room. Brand carries the velocity from the
        // briefing into live tracking — no jarring context switch.
        handoffTimerRef.current = setTimeout(() => {
          setAct4Phase('handoff');
          setNarratorLine('Handing off to the Wave Control Room…');
          // Mark this brand has seen the cinematic so revisiting the
          // briefing route doesn't replay it. Keyed by wave_id so a
          // different wave still gets its own first-time reveal.
          try {
            localStorage.setItem(`briefing_seen::${briefing.wave_id}`, '1');
          } catch (_e) { /* localStorage may be disabled */ }
          // Final 600ms slide-up before navigation, then hand off to
          // the Control Room with rich continuity state so it can render
          // the "Wave ignited" pill + seed the Pulse meter with the
          // pre-polled value + render the actual wave name and creator
          // count INSTANTLY (no "Untitled Wave · 0 creators" flash).
          // Every value passed here is FACTUAL (no speculation): the
          // wave already has a name, the cohort already has a member
          // count, the projected pulse target came from the same CMD
          // source as the briefing's PulseMeter. Composites stay
          // unseeded — those are honestly 0 until the wave engine
          // publishes its first tick.
          setTimeout(() => {
            const pzName = briefing.act3?.patient_zero?.name || null;
            const candidates = briefing.act1?.candidates || [];
            navigate(
              `/waves/${briefing.wave_id}/control`,
              {
                state: {
                  fromIgnition: true,
                  pzName,
                  prePulse: livePulse,
                  // ↓ Skeleton seed values — render instantly so the
                  //   brand never sees "Untitled Wave · 0 creators".
                  waveName: waveName || briefing.wave_name || null,
                  creatorCount: candidates.length,
                  projectedPulseTarget: briefing.act4?.projected_pulse_target ?? null,
                  compoundingLabel:     briefing.act3?.compounding_label || null,
                },
              },
            );
          }, 600);
        }, 2600);
      }
    }, 1000);
  }, [briefing, navigate, livePulse, waveName]);

  const handleFireWave = useCallback(() => {
    if (!briefing) return;
    if (hasFailingCheck()) {
      // Soft warning — give the brand a chance to reconsider, then
      // fire anyway if they confirm. Never block them.
      setIntegrityWarnOpen(true);
      return;
    }
    startCountdown();
  }, [briefing, hasFailingCheck, startCountdown]);

  if (!briefing) {
    return <BriefingLoadingState />;
  }

  return (
    <div
      className="min-h-screen bg-[#050505] text-white relative overflow-hidden"
      data-testid="briefing-canvas"
      data-act={act}
      data-act4-phase={act4Phase}
      style={{
        // Whole canvas slides up during handoff so the transition into
        // the Wave Control Room reads as continuous motion, not a
        // route change. 600ms matches the navigate() delay above.
        transform: act4Phase === 'handoff' ? 'translateY(-32px)' : 'translateY(0)',
        opacity:   act4Phase === 'handoff' ? 0.0 : 1.0,
        transition: 'transform 600ms cubic-bezier(0.4, 0, 0.2, 1), opacity 600ms ease-out',
      }}
    >
      {/* Header strip — minimal, always visible */}
      <div className="absolute top-0 left-0 right-0 z-30 flex items-center justify-between px-6 py-3 backdrop-blur-md bg-black/30 border-b border-white/5">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-bold text-gray-500 hover:text-white transition-colors"
            data-testid="briefing-back"
          >
            <ChevronLeft className="h-3 w-3" /> Back
          </button>
          <div className="h-3 w-px bg-white/10" />
          <p className="text-[10px] uppercase tracking-[0.18em] font-black text-cyan-400">
            Wave Briefing
          </p>
          <span className="text-[10px] text-gray-600">·</span>
          <p className="text-[11px] font-bold text-white truncate max-w-[200px]">
            {brandName || 'Brand'}
          </p>
          {waveName && (
            <>
              <span className="text-[10px] text-gray-600">·</span>
              <p className="text-[11px] text-gray-400 truncate max-w-[280px]">
                {waveName}
              </p>
            </>
          )}
        </div>

        {skipAllowed && act < 4 && (
          <button
            onClick={handleSkip}
            data-testid="briefing-skip"
            className="text-[10px] uppercase tracking-[0.18em] font-bold text-gray-500 hover:text-white transition-colors"
          >
            Skip animation →
          </button>
        )}
      </div>

      {/* Canvas surface — the additive zone where all 4 acts compose */}
      <div className="pt-14 pb-20 min-h-screen flex flex-col relative">
        {/* ACT 1 — DNA Sourcing. Gallery is replaced by the radial
            network when Act 2 fires; chips + hero stay anchored at
            the top through all subsequent acts. */}
        <Act1Dna
          fingerprint={briefing.act1.fingerprint}
          candidates={briefing.act1.candidates}
          survivors={briefing.act1.survivors}
          rejectedSample={briefing.act1.rejected_sample}
          cohortHero={briefing.act1.cohort_hero}
          tickedRingKeys={tickedRingKeys}
          galleryHidden={act >= 2}
          onComplete={handleAct1Complete}
          isActive={act === 1}
          data-testid="briefing-act-1"
        />

        {/* RADIAL NETWORK — Acts 2 + 3 unified. The Act 1 gallery
            shields lift off and reorganize into a radial map where
            every creator is still a recognizable person. The network
            runs the integrity checks (Act 2) and the burst choreography
            (Act 3) as one continuous reveal. */}
        {act >= 2 && briefing.act2 && briefing.act3 && (
          <div className="px-4" style={{ marginTop: 16 }}>
            <RadialNetwork
              candidates={briefing.act1.candidates}
              overlapEdges={briefing.act2.edges}
              integrity={briefing.act2.integrity}
              patientZero={briefing.act3.patient_zero}
              roles={briefing.act3.roles}
              schedule={briefing.act3.schedule}
              predictedCurve={briefing.act3.predicted_curve}
              compoundingLabel={briefing.act3.compounding_label}
              onCheckTick={handleRadialCheckTick}
              onAct2Complete={handleAct2Complete}
              onStageChange={handleRadialStageChange}
              onAct3Complete={handleRadialComplete}
              isActive={act >= 2}
              act4Phase={act4Phase}
              pulseTarget={briefing.act4?.projected_pulse_target ?? null}
              pulseCurrent={livePulse ?? 0}
            />
          </div>
        )}

        {/* ACT 4 — Fire-the-Wave CTA. Materializes once Act 3 settles.
            Floats at the bottom-right of the viewport, OUT of the
            constellation's center so Patient Zero stays visible. The
            button owns the 3-2-1 countdown sweep; ignition visuals
            live on the RadialNetwork. */}
        {act3Settled && act4Phase !== 'handoff' && (
          <div
            data-testid="act4-fire-zone"
            className="fixed z-30 flex flex-col items-center"
            style={{
              bottom: 96,
              right: 64,
              animation: 'fireZoneRise 700ms cubic-bezier(0.4, 0, 0.2, 1) both',
            }}
          >
            <IgnitionFireButton
              phase={act4Phase}
              countdownN={countdownN}
              onFire={handleFireWave}
            />
          </div>
        )}

        <style>{`
          @keyframes fireZoneRise {
            from { opacity: 0; transform: translateY(24px); }
            to   { opacity: 1; transform: translateY(0); }
          }
        `}</style>

        {/* Spacer — keeps the layout flow consistent. */}
        <div data-testid="briefing-acts-pending" className="flex-1" />
      </div>

      {/* Footer — narrator line (always single line, swaps on act change) */}
      <div className="fixed bottom-0 left-0 right-0 z-30 px-6 py-4 backdrop-blur-md bg-black/40 border-t border-white/5">
        <p
          data-testid="briefing-narrator"
          className="text-center text-[14px] text-white leading-snug font-medium"
        >
          {narratorLine}
        </p>
      </div>

      {/* INTEGRITY WARNING — soft confirmation when the brand fires
          with a failing structural check. The brand stays in control:
          they see the specific risk and choose to fire or cancel.
          Not a hard block. */}
      {integrityWarnOpen && (
        <div
          data-testid="integrity-warn-modal"
          className="fixed inset-0 z-40 flex items-center justify-center backdrop-blur-md"
          style={{ background: 'rgba(0,0,0,0.65)' }}
        >
          <div
            className="max-w-md w-full mx-6 rounded-2xl p-6"
            style={{
              background: '#0a0a0a',
              border:     '1px solid rgba(255,77,148,0.40)',
              boxShadow:  '0 0 60px rgba(255,77,148,0.30)',
            }}
          >
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="h-6 w-6 flex-shrink-0" style={{ color: '#F59E0B' }} />
              <div>
                <h3 className="text-[16px] font-black text-white mb-1">
                  Cohort has a weak structural check
                </h3>
                <p className="text-[13px] text-gray-300 leading-relaxed">
                  {(() => {
                    const fc = (briefing.act2?.integrity?.checks || []).find(c => !c.passed);
                    if (!fc) return 'One or more integrity checks failed. The wave will still fire if you proceed, but the predicted compounding effect may be weaker than benchmark.';
                    return `${fc.label}: ${fc.detail}${fc.benchmark ? ` · benchmark ${fc.benchmark}` : ''}. The wave will still fire if you proceed, but predicted compounding may be weaker.`;
                  })()}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 mt-5">
              <button
                onClick={() => setIntegrityWarnOpen(false)}
                data-testid="integrity-warn-cancel"
                className="px-4 py-2 rounded-lg text-[12px] font-bold text-gray-300 hover:text-white transition"
              >
                Cancel
              </button>
              <button
                onClick={startCountdown}
                data-testid="integrity-warn-confirm"
                className="px-5 py-2 rounded-lg text-[12px] font-black uppercase tracking-[0.08em]"
                style={{
                  background: '#FF4D94',
                  color: '#FFFFFF',
                  boxShadow: '0 0 20px rgba(255,77,148,0.50)',
                }}
              >
                Fire anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
